<template>
  <div class="sidebar" :class="{ collapsed, 'mobile-open': mobileMenuOpen }">
    <div class="sidebar-header">
      <button class="sidebar-toggle" @click="toggleSidebar">
        <i class="bi" :class="collapsed ? 'bi-chevron-right' : 'bi-chevron-left'"></i>
      </button>
      <!-- Debug: {{ settingsStore.logoUrl }} -->
      <img :src="settingsStore.logoUrl" :alt="`Intranet Logo (${settingsStore.logoUrl})`" class="logo-img" @error="handleLogoError">
      <h3>{{ settingsStore.organizationName }}</h3>
      <p>Company Portal</p>
    </div>

    <nav class="sidebar-nav">
      <!-- Company Section -->
      <div v-show="showCompanySection" class="nav-section">
        <div class="nav-section-header" @click="toggleSection('company')">
          <div class="nav-section-title">
            <i class="bi bi-building section-icon"></i>
            <span>Company</span>
          </div>
          <i class="bi bi-chevron-down toggle-icon" :class="{ rotated: !expandedSections.company }"></i>
        </div>
        <div v-show="expandedSections.company" class="nav-submenu">
          <router-link v-show="canAccess('company', 'news')" to="/company/wall" class="nav-link">
            <i class="bi bi-megaphone"></i>
            <span>News & Updates</span>
          </router-link>
          <router-link v-show="canAccess('company', 'orgchart')" to="/company/orgchart" class="nav-link">
            <i class="bi bi-diagram-3"></i>
            <span>OrgChart</span>
          </router-link>
          <router-link v-show="canAccess('company', 'okrs')" to="/company/okrs" class="nav-link">
            <i class="bi bi-bullseye"></i>
            <span>OKRs</span>
          </router-link>
          <!-- Compliance and Tools are hidden until their pages are built
               (both currently dead-end on an "Under Development" stub). -->
          <router-link v-show="canAccess('company', 'feedback-insights')" to="/company/feedback-insights" class="nav-link">
            <i class="bi bi-bar-chart-line"></i>
            <span>Feedback Insights</span>
          </router-link>
        </div>
      </div>

      <!-- Team Management Section -->
      <div v-show="showTeamSection" class="nav-section">
        <div class="nav-section-header" @click="toggleSection('team')">
          <div class="nav-section-title">
            <i class="bi bi-people section-icon"></i>
            <span>Team Management</span>
          </div>
          <i class="bi bi-chevron-down toggle-icon" :class="{ rotated: !expandedSections.team }"></i>
        </div>
        <div v-show="expandedSections.team" class="nav-submenu">
          <router-link v-show="canAccess('team', 'directory')" to="/team/directory" class="nav-link">
            <i class="bi bi-diagram-3"></i>
            <span>My Team</span>
          </router-link>
          <router-link v-show="canAccess('team', 'timesheet-approvals')" to="/team/timesheet-approvals" class="nav-link">
            <i class="bi bi-clock-history"></i>
            <span>Timesheet Approvals</span>
          </router-link>
          <router-link v-show="canAccess('team', 'leave-approvals')" to="/team/leave-approvals" class="nav-link">
            <i class="bi bi-calendar-check"></i>
            <span>Leave Approvals</span>
          </router-link>
          <router-link v-show="canAccess('team', 'performance-reviews')" to="/team/performance-reviews" class="nav-link">
            <i class="bi bi-star"></i>
            <span>My Performance</span>
          </router-link>
          <router-link v-show="canAccess('team', 'projects')" to="/team/projects" class="nav-link">
            <i class="bi bi-kanban"></i>
            <span>Projects</span>
          </router-link>
        </div>
      </div>

      <!-- Financials Section -->
      <div v-show="showFinancialsSection" class="nav-section">
        <div class="nav-section-header" @click="toggleSection('financials')">
          <div class="nav-section-title">
            <i class="bi bi-calculator section-icon"></i>
            <span>Financials</span>
          </div>
          <i class="bi bi-chevron-down toggle-icon" :class="{ rotated: !expandedSections.financials }"></i>
        </div>
        <div v-show="expandedSections.financials" class="nav-submenu">
          <router-link v-show="canAccess('financials', 'allocation')" to="/financials/allocation" class="nav-link">
            <i class="bi bi-person-workspace"></i>
            <span>Resource Allocation</span>
          </router-link>
          <router-link v-show="canAccess('financials', 'revenue')" to="/financials/revenue-tracker" class="nav-link">
            <i class="bi bi-currency-dollar"></i>
            <span>Revenue Tracker</span>
          </router-link>
          <router-link v-show="canAccess('financials', 'costs')" to="/financials/costs" class="nav-link">
            <i class="bi bi-cash-stack"></i>
            <span>Costs</span>
          </router-link>
          <router-link v-show="canAccess('financials', 'analytics')" to="/financials/analytics" class="nav-link">
            <i class="bi bi-graph-up"></i>
            <span>Analytics</span>
          </router-link>
        </div>
      </div>

      <!-- Delivery Portal Section -->
      <div v-show="showDeliverySection" class="nav-section">
        <div class="nav-section-header" @click="toggleSection('delivery')">
          <div class="nav-section-title">
            <i class="bi bi-box-arrow-in-right section-icon"></i>
            <span>Delivery Portal</span>
          </div>
          <i class="bi bi-chevron-down toggle-icon" :class="{ rotated: !expandedSections.delivery }"></i>
        </div>
        <div v-show="expandedSections.delivery" class="nav-submenu">
          <router-link
            v-for="subsection in accessibleDeliverySubsections"
            :key="subsection.slug"
            :to="`/delivery/${subsection.slug}`"
            class="nav-link"
          >
            <i :class="getDeliveryIcon(subsection.sourceSubsection)"></i>
            <span>{{ subsection.displayName }}</span>
          </router-link>
        </div>
      </div>

      <!-- My Workspace Section -->
      <div v-show="showWorkspaceSection" class="nav-section">
        <div class="nav-section-header" @click="toggleSection('workspace')">
          <div class="nav-section-title">
            <i class="bi bi-person-circle section-icon"></i>
            <span>My Workspace</span>
          </div>
          <i class="bi bi-chevron-down toggle-icon" :class="{ rotated: !expandedSections.workspace }"></i>
        </div>
        <div v-show="expandedSections.workspace" class="nav-submenu">
          <!-- Overview - visible if subsection is activated -->
          <a v-show="isSectionActive('workspace', 'overview')" href="/workspace#overview" class="nav-link" @click="navigateToWorkspaceSection('overview', $event)">
            <i class="bi bi-person-badge"></i>
            <span>Overview</span>
          </a>
          <!-- Other sections - permission and activation controlled -->
          <a v-show="canAccess('workspace', 'experience')" href="/workspace#experience" class="nav-link" @click="navigateToWorkspaceSection('experience', $event)">
            <i class="bi bi-briefcase"></i>
            <span>Experience</span>
          </a>
          <a v-show="canAccess('workspace', 'education')" href="/workspace#education" class="nav-link" @click="navigateToWorkspaceSection('education', $event)">
            <i class="bi bi-mortarboard"></i>
            <span>Education</span>
          </a>
          <a v-show="canAccess('workspace', 'performance')" href="/workspace#performance" class="nav-link" @click="navigateToWorkspaceSection('performance', $event)">
            <i class="bi bi-graph-up-arrow"></i>
            <span>My Performance</span>
          </a>
          <a v-show="canAccess('workspace', 'timesheets')" href="/workspace#timesheets" class="nav-link" @click="navigateToWorkspaceSection('timesheets', $event)">
            <i class="bi bi-clock"></i>
            <span>My Timesheets</span>
          </a>
          <a v-show="canAccess('workspace', 'leave')" href="/workspace#leave" class="nav-link" @click="navigateToWorkspaceSection('leave', $event)">
            <i class="bi bi-calendar-x"></i>
            <span>Leave Requests</span>
          </a>
          <a v-show="canAccess('workspace', 'compensation')" href="/workspace#compensation" class="nav-link" @click="navigateToWorkspaceSection('compensation', $event)">
            <i class="bi bi-cash-coin"></i>
            <span>Compensation</span>
          </a>
          <a v-show="canAccess('workspace', 'documents')" href="/workspace#documents" class="nav-link" @click="navigateToWorkspaceSection('documents', $event)">
            <i class="bi bi-file-earmark-text"></i>
            <span>Documents</span>
          </a>
          <a v-show="canAccess('workspace', 'messages')" href="/workspace#messages" class="nav-link" @click="navigateToWorkspaceSection('messages', $event)">
            <i class="bi bi-chat-dots"></i>
            <span>Messages</span>
          </a>
          <a v-show="canAccess('workspace', 'my-network')" href="/workspace#my-network" class="nav-link" @click="navigateToWorkspaceSection('my-network', $event)">
            <i class="bi bi-people"></i>
            <span>My Network</span>
          </a>
        </div>
      </div>

      <!-- Administration Section -->
      <div v-show="showAdminSection" class="nav-section">
        <div class="nav-section-header" @click="toggleSection('admin')">
          <div class="nav-section-title">
            <i class="bi bi-gear-fill section-icon"></i>
            <span>Administration</span>
          </div>
          <i class="bi bi-chevron-down toggle-icon" :class="{ rotated: !expandedSections.admin }"></i>
        </div>
        <div v-show="expandedSections.admin" class="nav-submenu">
          <router-link v-show="canAccess('admin', 'dashboard')" to="/admin/dashboard" class="nav-link">
            <i class="bi bi-speedometer2"></i>
            <span>System Dashboard</span>
          </router-link>
          <router-link v-show="canAccess('admin', 'certifications')" to="/admin/certifications" class="nav-link">
            <i class="bi bi-award-fill"></i>
            <span>Certification Catalog</span>
          </router-link>
          <router-link v-show="canAccess('admin', 'users')" to="/admin/users" class="nav-link">
            <i class="bi bi-people-fill"></i>
            <span>User Management</span>
          </router-link>
          <router-link v-show="canAccess('admin', 'access')" to="/admin/access" class="nav-link">
            <i class="bi bi-key"></i>
            <span>Access Control</span>
          </router-link>
          <router-link v-show="canAccess('admin', 'audit')" to="/admin/audit" class="nav-link">
            <i class="bi bi-file-text-fill"></i>
            <span>Audit Logs</span>
          </router-link>
          <router-link v-show="canAccess('admin', 'settings')" to="/admin/settings" class="nav-link">
            <i class="bi bi-gear-fill"></i>
            <span>Settings</span>
          </router-link>
          <router-link v-show="canAccess('admin', 'delivery-portal')" to="/admin/delivery-portal" class="nav-link">
            <i class="bi bi-box-arrow-in-right"></i>
            <span>Delivery Portal</span>
          </router-link>
        </div>
      </div>
    </nav>

    <div class="sidebar-footer">
      <div class="footer-icon-row">
        <a href="https://dvloper.io" target="_blank" rel="noopener noreferrer" class="footer-icon-btn" v-tooltip="'dvloper.io'">
          <i class="bi bi-globe"></i>
        </a>
        <router-link to="/install-app" class="footer-icon-btn" v-tooltip="'Install App'">
          <i class="bi bi-phone"></i>
        </router-link>
        <button class="footer-icon-btn" @click="handleLogout" v-tooltip="'Logout'">
          <i class="bi bi-box-arrow-right"></i>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, inject, onMounted } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import { useSettingsStore } from '@/stores/settings';
import { useConnectionsStore } from '@/stores/connections';
import { useDeliveryPortalStore } from '@/stores/deliveryPortal';

const props = defineProps({
  mobileMenuOpen: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['close-mobile-menu']);

const router = useRouter();
const route = useRoute();
const authStore = useAuthStore();
const settingsStore = useSettingsStore();
const connectionsStore = useConnectionsStore();
const deliveryPortalStore = useDeliveryPortalStore();

const collapsed = inject('sidebarCollapsed');
const isMobile = inject('isMobile', ref(false));
const expandedSections = ref({
  company: true,
  team: true,
  financials: true,
  delivery: true,
  workspace: true,
  admin: true,
});

// Use shared store for pending connections count
const pendingConnectionsCount = computed(() => connectionsStore.pendingCount);

const isAdmin = computed(() => authStore.isAdmin);

// Helper to check if user has permission
const hasPermission = (section, subsection) => {
  return authStore.hasPermission(section, subsection);
};

// Helper to check if section/subsection is activated
const isSectionActive = (section, subsection) => {
  return authStore.isSectionActive(section, subsection);
};

// Combined check: must have both permission AND section must be active
const canAccess = (section, subsection) => {
  return isSectionActive(section, subsection) && hasPermission(section, subsection);
};

// Helper to check if user has any permission in a section (section-level OR any subsection)
// Used for sidebar visibility where having any subsection access should show the section
const hasAnyPermissionInSection = (section) => {
  return authStore.hasAnyPermissionInSection(section);
};

// Computed properties for section visibility
// Section must be activated AND user must have access to at least one subsection
const showCompanySection = computed(() => {
  // Section must be activated first
  if (!isSectionActive('company', null)) return false;
  // Then check permissions (admins always have permission)
  if (isAdmin.value) return true;
  return canAccess('company', 'news') ||
         canAccess('company', 'compliance') ||
         canAccess('company', 'resources') ||
         canAccess('company', 'orgchart') ||
         canAccess('company', 'okrs') ||
         canAccess('company', 'feedback-insights');
});

const showTeamSection = computed(() => {
  if (!isSectionActive('team', null)) return false;
  if (isAdmin.value) return true;
  return canAccess('team', 'directory') ||
         canAccess('team', 'timesheet-approvals') ||
         canAccess('team', 'leave-approvals') ||
         canAccess('team', 'performance-reviews') ||
         canAccess('team', 'projects');
});

const showFinancialsSection = computed(() => {
  if (!isSectionActive('financials', null)) return false;
  if (isAdmin.value) return true;
  return canAccess('financials', 'allocation') ||
         canAccess('financials', 'revenue') ||
         canAccess('financials', 'costs') ||
         canAccess('financials', 'analytics');
});

const showWorkspaceSection = computed(() => {
  // Workspace section visibility depends on activation
  if (!isSectionActive('workspace', null)) return false;
  // If activated, it's visible (overview is always accessible for authenticated users)
  return true;
});

// Get all configured delivery subsections from store (for admins)
const allDeliverySubsections = computed(() => {
  return deliveryPortalStore.allConfiguredSubsections || [];
});

// Get accessible delivery subsections from portal permission system
const accessibleDeliverySubsections = computed(() => {
  // Admins see all configured subsections
  if (isAdmin.value) return allDeliverySubsections.value;

  // For non-admins, use the portal's permission system (from fetchMyAccess)
  // This returns only subsections the user has tab permissions for
  return deliveryPortalStore.accessibleSubsections || [];
});

// Delivery Portal section visibility
const showDeliverySection = computed(() => {
  // Check if delivery section is activated globally
  if (!isSectionActive('delivery', null)) return false;
  // User must have Access Control permission for 'delivery' section (or any subsection)
  // Uses hasAnyPermissionInSection which checks for section-level OR any subsection permission
  if (!hasAnyPermissionInSection('delivery')) return false;
  // Admins always have access if there are subsections configured
  if (isAdmin.value) return allDeliverySubsections.value.length > 0;
  // Non-admins: show if they have portal access to at least one subsection
  return accessibleDeliverySubsections.value.length > 0;
});

// Get icon for delivery subsection based on source
function getDeliveryIcon(sourceSubsection) {
  const icons = {
    'revenue': 'bi bi-currency-dollar',
    'costs': 'bi bi-cash-stack',
    'timesheet-approvals': 'bi bi-clock-history',
    'leave-approvals': 'bi bi-calendar-check'
  };
  return icons[sourceSubsection] || 'bi bi-folder';
}

const showAdminSection = computed(() => {
  if (!isSectionActive('admin', null)) return false;
  if (isAdmin.value) return true;
  return canAccess('admin', 'dashboard') ||
         canAccess('admin', 'certifications') ||
         canAccess('admin', 'users') ||
         canAccess('admin', 'access') ||
         canAccess('admin', 'audit') ||
         canAccess('admin', 'settings');
});

function toggleSidebar() {
  collapsed.value = !collapsed.value;
}

function toggleSection(section) {
  expandedSections.value[section] = !expandedSections.value[section];
}

async function handleLogout() {
  await authStore.logout();
  router.push('/login');
}

function handleLogoError(event) {
  // Fallback to default logo if the image fails to load
  event.target.src = '/logo.png';
}

// Navigate to workspace section with smooth scrolling
function navigateToWorkspaceSection(sectionId, event) {
  event.preventDefault();

  // Close mobile menu when navigating
  if (isMobile.value) {
    emit('close-mobile-menu');
  }

  // If we're already on the workspace page, just scroll to the section
  if (route.path === '/workspace') {
    // Update the URL hash
    window.location.hash = sectionId;

    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 100; // Account for sticky nav
      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
      window.scrollTo({
        top: elementPosition - offset,
        behavior: 'smooth'
      });
    }
  } else {
    // Navigate to workspace with hash, the WorkspaceView will handle scrolling on mount
    router.push(`/workspace#${sectionId}`);
  }
}

// Load settings when sidebar mounts
onMounted(async () => {
  await settingsStore.loadSettings();
  // Fetch pending connections count from shared store
  await connectionsStore.fetchPendingCount();
  // Fetch delivery portal access if section is active
  // Only fetch full config for admins (fetchConfig is admin-only)
  if (isSectionActive('delivery', null)) {
    await deliveryPortalStore.fetchMyAccess();
    if (isAdmin.value) {
      await deliveryPortalStore.fetchConfig();
    }
  }
});
</script>

<style scoped>
.sidebar {
  position: fixed;
  top: 0;
  left: 0;
  height: 100vh;
  width: var(--sidebar-width);
  background: linear-gradient(180deg, var(--primary-color) 0%, var(--secondary-color) 100%);
  color: var(--dark-text);
  padding: 0;
  z-index: 1000;
  box-shadow: 2px 0 10px rgba(0, 0, 0, 0.1);
  transition: width 0.3s ease;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.sidebar.collapsed {
  width: var(--sidebar-collapsed-width);
}

.sidebar-header {
  padding: 30px 20px;
  background: rgba(255, 255, 255, 0.1);
  border-bottom: 1px solid rgba(45, 49, 66, 0.1);
  text-align: center;
  position: relative;
}

.logo-img {
  height: 60px;
  width: auto;
  margin-bottom: 10px;
  transition: all 0.3s ease;
}

.sidebar.collapsed .logo-img {
  height: 40px;
  margin-bottom: 0;
}

.sidebar-header h3 {
  margin: 0;
  font-size: 24px;
  font-weight: 700;
  color: var(--dark-text);
  white-space: nowrap;
  opacity: 1;
  transition: opacity 0.3s ease;
}

.sidebar.collapsed .sidebar-header h3 {
  opacity: 0;
  position: absolute;
}

.sidebar-header p {
  margin: 5px 0 0 0;
  font-size: 14px;
  color: var(--dark-text);
  opacity: 0.7;
  transition: opacity 0.3s ease;
}

.sidebar.collapsed .sidebar-header p {
  opacity: 0;
  position: absolute;
}

.sidebar-toggle {
  position: absolute;
  top: 15px;
  right: 15px;
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: var(--dark-text);
  width: 35px;
  height: 35px;
  border-radius: var(--radius-md);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  font-size: 18px;
  z-index: 20;
}

.sidebar-toggle:hover {
  background: rgba(255, 255, 255, 0.3);
}

.sidebar.collapsed .sidebar-toggle {
  position: fixed;
  top: 50%;
  right: auto;
  left: calc(var(--sidebar-collapsed-width) - 17px);
  transform: translateY(-50%);
  width: 34px;
  height: 60px;
  border-radius: 0 var(--radius-lg) var(--radius-lg) 0;
  background: linear-gradient(135deg, var(--primary-color) 0%, var(--secondary-color) 100%);
  box-shadow: 2px 0 8px rgba(0, 0, 0, 0.2);
  border-left: 3px solid var(--accent-color);
}

.sidebar.collapsed .sidebar-toggle:hover {
  left: calc(var(--sidebar-collapsed-width) - 15px);
  box-shadow: 3px 0 12px rgba(0, 0, 0, 0.3);
}

.sidebar-nav {
  flex: 1;
  padding: 20px 0;
  overflow-y: auto;
}

.sidebar-nav::-webkit-scrollbar {
  width: 6px;
}

.sidebar-nav::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.1);
}

.sidebar-nav::-webkit-scrollbar-thumb {
  background: rgba(45, 49, 66, 0.3);
  border-radius: var(--radius-xs);
}

.nav-section {
  margin-bottom: 5px;
}

.nav-section-header {
  color: var(--dark-text);
  padding: 12px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  transition: all 0.3s ease;
  font-weight: 600;
  font-size: 13px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  background: rgba(255, 255, 255, 0.05);
  white-space: nowrap;
  overflow: hidden;
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
}

.nav-section-header:hover {
  background: rgba(255, 255, 255, 0.15);
}

.sidebar.collapsed .nav-section-header {
  justify-content: center;
  padding: 12px 10px;
}

.nav-section-title {
  display: flex;
  align-items: center;
  gap: 12px;
  font-weight: 600;
  user-select: none;
}

.nav-section-title span {
  transition: opacity 0.3s ease;
}

.sidebar.collapsed .nav-section-title span {
  opacity: 0;
  width: 0;
}

.section-icon {
  font-size: 18px;
  margin-right: 10px;
}

.toggle-icon {
  font-size: 12px;
  transition: transform 0.3s ease, opacity 0.3s ease;
}

.toggle-icon.rotated {
  transform: rotate(-90deg);
}

.sidebar.collapsed .nav-section-header .toggle-icon {
  opacity: 0;
  width: 0;
}

.nav-submenu {
  padding-left: 0;
  overflow: hidden;
  transition: max-height 0.3s ease;
  background: rgba(0, 0, 0, 0.1);
}

.sidebar.collapsed .nav-submenu {
  display: none;
}

.nav-submenu .nav-link {
  padding: 12px 20px 12px 50px;
  font-size: 14px;
  font-weight: 500;
}

.nav-link {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 20px;
  color: var(--dark-text);
  text-decoration: none;
  transition: all 0.2s ease;
  border-left: 3px solid transparent;
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  white-space: nowrap;
  overflow: hidden;
}

.nav-link:hover {
  background: rgba(255, 255, 255, 0.15);
  border-left-color: var(--dark-text);
}

.nav-link.router-link-active {
  background: rgba(255, 255, 255, 0.2);
  border-left-color: var(--dark-text);
  font-weight: 600;
}

.nav-link i {
  font-size: 16px;
  width: 20px;
}

.sidebar-footer {
  padding: 20px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.footer-icon-row {
  display: flex;
  justify-content: center;
  gap: 8px;
}

.footer-icon-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: rgba(255, 255, 255, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: var(--radius-full);
  color: var(--dark-text);
  text-decoration: none;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.footer-icon-btn:hover {
  background: rgba(255, 255, 255, 0.25);
  border-color: rgba(255, 255, 255, 0.3);
  color: var(--dark-text);
}

.footer-icon-btn.router-link-active {
  background: rgba(255, 255, 255, 0.3);
  border-color: var(--dark-text);
}

.sidebar.collapsed .sidebar-footer {
  padding: 15px 10px;
}

/* Connection notification badge */
.nav-badge {
  background: var(--accent-color, #e74c3c);
  color: white;
  font-size: 11px;
  font-weight: 600;
  min-width: 18px;
  height: 18px;
  border-radius: 9px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 5px;
  margin-left: auto;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}

.sidebar.collapsed .nav-badge {
  display: none;
}

/* Mobile Responsive Styles */
@media (max-width: 768px) {
  .sidebar {
    transform: translateX(-100%);
    width: 100%;
    max-width: 100vw;
  }

  .sidebar.mobile-open {
    transform: translateX(0);
  }

  .sidebar.collapsed {
    width: 100%;
    max-width: 100vw;
    transform: translateX(-100%);
  }

  .sidebar.collapsed.mobile-open {
    transform: translateX(0);
  }

  /* Hide desktop toggle on mobile */
  .sidebar-toggle {
    display: none;
  }

  /* Show all content in mobile view (not collapsed) */
  .sidebar.collapsed .sidebar-header h3,
  .sidebar.collapsed .sidebar-header p,
  .sidebar.collapsed .nav-section-title span,
  .sidebar.collapsed .nav-section-header .toggle-icon,
  .sidebar.collapsed .nav-submenu,
  .sidebar.collapsed .sidebar-footer .logout-text {
    opacity: 1;
    position: static;
    width: auto;
    display: block;
  }

  .sidebar.collapsed .nav-submenu {
    display: block;
  }

  .sidebar.collapsed .sidebar-footer .btn-outline-light {
    width: 100%;
    height: auto;
    border-radius: var(--radius-md);
    padding: 10px 16px;
  }

  .sidebar.collapsed .logo-img {
    height: 60px;
    margin-bottom: 10px;
  }

  .sidebar.collapsed .nav-section-header {
    justify-content: space-between;
    padding: 12px 20px;
  }

  .sidebar.collapsed .footer-icon-row {
    justify-content: center;
  }
}
</style>
